import React, { useState } from "react";
import { ArrowRight, CheckCircle2, Mail, Linkedin, CalendarDays } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { content } from "../data/content";
import SectionHeading from "./SectionHeading";
import { toast } from "../hooks/use-toast";
import { openBooking } from "../utils/booking";

const altIcons = { email: Mail, linkedin: Linkedin, calendar: CalendarDays };

const ContactSection = React.forwardRef((props, ref) => {
  const { lang } = useLanguage();
  const t = content[lang].contact;
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", org: "", phone: "", role: "", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.org) {
      toast({ title: t.required });
      return;
    }
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang, source: "website contact form" }),
      });
      if (!res.ok) throw new Error("request failed");
      setSent(true);
      toast({ title: t.success });
    } catch (err) {
      toast({ title: t.error || "Ocurrió un error. Probá de nuevo o escribinos por email." });
    }
  };

  const inputCls =
    "w-full rounded-md border border-white/12 bg-narwhal-dark/60 px-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-narwhal-teal focus:ring-1 focus:ring-narwhal-teal transition-colors";

  return (
    <section id="contacto" ref={ref} data-testid="contact-section" className="relative py-24 md:py-28 scroll-mt-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_50%,rgba(124,231,212,0.08),transparent_55%)]" />
      <div className="relative max-w-6xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <SectionHeading kicker={t.kicker} title={t.title} accent={t.titleAccent} body={t.body} />
          <div className="mt-8">
            <span className="text-sm text-white/80">{t.altTitle}</span>
            <div className="mt-4 space-y-3">
              {t.alts.map((a) => {
                const Icon = altIcons[a.type];
                if (a.type === "calendar") {
                  return (
                    <button
                      key={a.label}
                      type="button"
                      onClick={openBooking}
                      data-testid={`contact-alt-${a.type}`}
                      className="flex items-center gap-3 text-sm text-narwhal-muted hover:text-narwhal-teal transition-colors"
                    >
                      <Icon className="w-4 h-4 text-narwhal-teal" />{a.label}
                    </button>
                  );
                }
                if (a.href) {
                  return (
                    <a
                      key={a.label}
                      href={a.href}
                      target={a.href.startsWith("http") ? "_blank" : undefined}
                      rel={a.href.startsWith("http") ? "noopener noreferrer" : undefined}
                      data-testid={`contact-alt-${a.type}`}
                      className="flex items-center gap-3 text-sm text-narwhal-muted hover:text-narwhal-teal transition-colors"
                    >
                      <Icon className="w-4 h-4 text-narwhal-teal" />{a.label}
                    </a>
                  );
                }
                return (
                  <div key={a.label} data-testid={`contact-alt-${a.type}`} className="flex items-center gap-3 text-sm text-narwhal-muted">
                    <Icon className="w-4 h-4 text-narwhal-teal" />{a.label}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-white/10 bg-narwhal-panel/60 p-7 md:p-8">
          {sent ? (
            <div className="flex flex-col items-center justify-center text-center py-12">
              <CheckCircle2 className="w-14 h-14 text-narwhal-teal" />
              <p className="mt-5 text-lg text-white" role="status" data-testid="contact-success-message">{t.success}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="contact-form">
              <input name="name" value={form.name} onChange={onChange} placeholder={t.fields.name} aria-label={t.fields.name} data-testid="contact-name-input" className={inputCls} />
              <input name="email" type="email" value={form.email} onChange={onChange} placeholder={t.fields.email} aria-label={t.fields.email} data-testid="contact-email-input" className={inputCls} />
              <input name="org" value={form.org} onChange={onChange} placeholder={t.fields.org} aria-label={t.fields.org} data-testid="contact-company-input" className={inputCls} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input name="phone" type="tel" value={form.phone} onChange={onChange} placeholder={t.fields.phone} aria-label={t.fields.phone} data-testid="contact-phone-input" className={inputCls} />
                <input name="role" value={form.role} onChange={onChange} placeholder={t.fields.role} aria-label={t.fields.role} data-testid="contact-role-input" className={inputCls} />
              </div>
              <textarea name="message" value={form.message} onChange={onChange} placeholder={t.fields.message} aria-label={t.fields.message} data-testid="contact-message-input" rows={3} className={inputCls} />
              <button type="submit" data-testid="contact-submit-button" className="group w-full inline-flex items-center justify-center gap-2 rounded-md bg-narwhal-teal px-6 py-3.5 text-narwhal-dark font-semibold hover:bg-white transition-colors">
                {t.submit}<ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
});

ContactSection.displayName = "ContactSection";
export default ContactSection;
